import { useEffect, useState } from "react";
import { Sparkle, CheckCircle, X, Megaphone } from "@phosphor-icons/react";
import { fetchWhatsNew, markWhatsNewSeen } from "@/lib/api";

/** Auto-shows on app mount to any signed-in user (admin or customer) who has
 * unseen changelog entries. Guests and first-time logins see nothing.
 *
 * Dismissing the modal (or clicking "Got it") calls `/whats-new/seen`, bumping
 * last_seen_at so the user won't see these items again. */
export default function WhatsNewModal({ user }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isGuest = !user?.email
    || user.role === "guest"
    || /@local\.guest$/i.test(user.email);

  useEffect(() => {
    if (!user || isGuest) return;
    let alive = true;
    fetchWhatsNew(user.email)
      .then(d => {
        if (!alive) return;
        if (d.items && d.items.length) {
          setItems(d.items);
          setOpen(true);
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, [user?.email, isGuest]);

  const close = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await markWhatsNewSeen(user.email);
    } catch (_) { /* ignore — best-effort */ }
    setOpen(false);
    setBusy(false);
  };

  if (!open || isGuest) return null;

  return (
    <div className="fixed inset-0 z-[1300] bg-slate-950/70 flex items-center justify-center p-3" onClick={close} data-testid="whats-new-modal">
      <div className="bg-white rounded-sm max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3 bg-gradient-to-r from-blue-700 to-blue-900 text-white">
          <div className="w-9 h-9 rounded-sm bg-white/15 flex items-center justify-center shrink-0 backdrop-blur-sm">
            <Sparkle size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold font-['Chivo'] leading-tight">What's new since you were last here</h3>
            <p className="text-[11px] text-blue-100/90">
              {items.length} update{items.length === 1 ? "" : "s"} · catch up in under a minute
            </p>
          </div>
          <button onClick={close} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-white/10 text-white/80" title="Dismiss">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="whats-new-list">
          {items.map((it) => (
            <div key={it.id} className="flex gap-3" data-testid={`whats-new-${it.id}`}>
              <div className="w-7 h-7 rounded-sm bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                <Megaphone size={14} weight="bold" className="text-blue-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900">{it.title}</div>
                <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{it.description}</div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {new Date(it.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <footer className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-3">
          <p className="text-[10px] text-slate-500 flex-1">
            Dismissing marks these as read. You'll only see new items the next time you log in.
          </p>
          <button onClick={close} disabled={busy} data-testid="whats-new-got-it"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
            <CheckCircle size={12} weight="bold" /> Got it
          </button>
        </footer>
      </div>
    </div>
  );
}
